'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const landlordItems = [
  { label: 'Why PDA?', href: '/why-pda', desc: 'Why the numbers say we\'re different' },
  { label: 'Landlord Services Chester', href: '/landlord-services-chester', desc: 'Full management, tenant find & let-only' },
  { label: 'Services', href: '/landlord-services', desc: 'Full legal & letting support' },
  { label: 'Property Management', href: '/property-management-services', desc: 'Full management, let-only & rent collection' },
  { label: 'Free Rental Valuation', href: '/free-rental-valuation-chester', desc: 'How much rent can I get? Free & no obligation' },
  { label: 'Portfolio Landlord', href: '/portfolio-landlord', desc: 'Multi-property management & yield tools' },
  { label: 'Switch to PDA', href: '/switch-to-pda', desc: 'Unhappy with your agent? Switch free' },
  { label: 'Valuation', href: '/request-valuation', desc: 'Free property valuation' },
  { label: 'Instant Valuation Tool', href: '/property-valuation', desc: 'AI-powered rental yield estimator' },
  { label: 'Rental Yield Calculator', href: '/rental-yield-calculator', desc: 'Calculate your investment returns' },
  { label: 'HMO Manager', href: '/hmo-manager', desc: 'HMO licensing & compliance' },
  { label: 'First-Time Landlord', href: '/first-time-landlord', desc: 'Getting started guide' },
  { label: 'Accidental Landlord', href: '/accidental-landlord', desc: 'Inherited or couldn\'t sell? We handle everything' },
  { label: 'Resources', href: '/resources', desc: 'Guides, templates & downloads' },
  { label: 'Case Studies', href: '/case-studies', desc: 'Arrears, evictions & compliance resolved' },
  { label: 'Landlord Newsletter', href: '/portfolio-landlord#yield-calculator', desc: 'RRA 2025 updates & market insights' },
];

const tenantItems = [
  { label: 'Services', href: '/tenant-services', desc: 'What we offer tenants' },
  { label: 'Book Viewing', href: '/book-viewing', desc: 'Arrange a property viewing' },
  { label: 'Zero Deposit', href: '/zero-deposit', desc: 'Move in without a deposit' },
  { label: 'Tenant Portal', href: '/tenant-portal', desc: 'Manage your tenancy online' },
];

const legalItems = [
  { label: 'Landlord Compliance', href: '/landlord-compliance', desc: 'EPC, gas safety, EICR & more' },
  { label: 'Renters Rights Act', href: '/renters-rights-act', desc: 'What landlords must know' },
  { label: 'Landlord FAQ', href: '/landlord-faq', desc: 'RRA 2025 questions answered' },
  { label: 'Fixed Fees', href: '/fixed-fees-landlords', desc: 'Transparent pricing' },
  { label: 'Section 21 & Eviction', href: '/section-21-eviction', desc: 'Notices & possession proceedings' },
  { label: 'Deposit Protection', href: '/tenancy-deposit-protection', desc: 'Compliance & dispute guidance' },
  { label: 'HMO Licensing', href: '/hmo-licensing', desc: 'Licensing & compliance' },
  { label: 'Regulatory Information', href: '/regulatory', desc: 'CMP cert, redress & complaints' },
  { label: 'Area Guides', href: '/area-guides', desc: 'Chester, Wirral & North Wales' },
  { label: 'Hoole', href: '/area-guides/hoole', desc: 'Renting & investing in Hoole' },
  { label: 'Handbridge', href: '/area-guides/handbridge', desc: 'Renting & investing in Handbridge' },
  { label: 'Boughton', href: '/area-guides/boughton', desc: 'Renting & investing in Boughton' },
  { label: 'Upton', href: '/area-guides/upton', desc: 'Renting & investing in Upton' },
  { label: 'Chester City Centre', href: '/area-guides/chester-city-centre', desc: 'Renting & investing in the city centre' },
];

type DropdownKey = 'landlords' | 'tenants' | 'legal' | null;

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [announcementVisible, setAnnouncementVisible] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState<DropdownKey>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (headerRef.current && !headerRef.current.contains(e.target as Node)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = (key: DropdownKey) => {
    setActiveDropdown((prev) => (prev === key ? null : key));
  };

  const DropdownMenu = ({ items }: { items: { label: string; href: string; desc: string }[] }) => (
    <div className="absolute top-full left-0 mt-2 w-64 bg-[#081525] border border-white/15 rounded-xl shadow-2xl overflow-hidden z-[100]">
      {items.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="flex flex-col px-5 py-3.5 hover:bg-[#1B6B5A]/30 transition-colors border-b border-white/8 last:border-0"
          onClick={() => setActiveDropdown(null)}
        >
          <span className="text-white text-sm font-medium">{item.label}</span>
          <span className="text-[#8A9BB0] text-xs mt-0.5">{item.desc}</span>
        </Link>
      ))}
    </div>
  );

  const ChevronIcon = ({ open }: { open: boolean }) => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );

  return (
    <>
      {/* ── Solicitor USP Announcement Bar ── */}
      {announcementVisible && (
        <div
          className="fixed top-0 left-0 w-full z-50 bg-[#1B6B5A] text-white"
          role="banner"
          aria-label="Unique selling point announcement"
        >
          <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between gap-4">
            <div className="flex items-center justify-center gap-2.5 flex-1 flex-wrap">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              <span className="text-xs font-semibold tracking-wide text-white/90 uppercase">
                Chester&apos;s Only Letting Agency
              </span>
              <span className="hidden sm:inline text-white/40 text-xs">|</span>
              <span className="text-xs font-medium text-white/85">
                with an <strong className="text-white font-semibold">in-house landlord&apos;s solicitor</strong> — legal protection built into your letting service.
              </span>
              <a
                href="#why-pda"
                className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-white/15 hover:bg-white/25 transition-colors px-2.5 py-1 rounded-full tracking-wide"
              >
                Find out more
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </a>
            </div>
            <button
              onClick={() => setAnnouncementVisible(false)}
              className="flex-shrink-0 text-white/60 hover:text-white transition-colors p-1 rounded"
              aria-label="Dismiss announcement"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <header
        ref={headerRef}
        className={`fixed left-0 right-0 z-40 h-20 ${
          scrolled
            ? 'bg-[#081525] backdrop-blur-xl border-b border-white/8'
            : 'bg-[#081525] border-b border-white/8'
        } header-transition`}
        style={{ top: announcementVisible ? '40px' : '0px' }}
      >
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group flex-shrink-0" aria-label="PDA Letting Agents Home">
            <div className="flex-shrink-0 group-hover:scale-105 transition-transform duration-300">
              <Image
                src="/assets/images/logo-1784731452793.png"
                alt="PDA Letting Agents logo"
                width={60}
                height={24}
                className="object-contain"
                priority
              />
            </div>
            <div className="flex flex-col">
              <span className="text-white font-display font-medium text-base leading-none tracking-wide">
                PDA Letting Agents
              </span>
              <span className="text-[#8A9BB0] text-[10px] font-sans font-medium tracking-[0.18em] uppercase leading-none mt-1">
                Chester &amp; North Wales
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">

            {/* Landlords dropdown */}
            <div className="relative">
              <button
                className="nav-link flex items-center gap-1"
                aria-expanded={activeDropdown === 'landlords'}
                aria-haspopup="true"
                onClick={() => toggleDropdown('landlords')}
              >
                Landlords
                <ChevronIcon open={activeDropdown === 'landlords'} />
              </button>
              {activeDropdown === 'landlords' && <DropdownMenu items={landlordItems} />}
            </div>

            {/* Tenants dropdown */}
            <div className="relative">
              <button
                className="nav-link flex items-center gap-1"
                aria-expanded={activeDropdown === 'tenants'}
                aria-haspopup="true"
                onClick={() => toggleDropdown('tenants')}
              >
                Tenants
                <ChevronIcon open={activeDropdown === 'tenants'} />
              </button>
              {activeDropdown === 'tenants' && <DropdownMenu items={tenantItems} />}
            </div>

            {/* Legal dropdown */}
            <div className="relative">
              <button
                className="nav-link flex items-center gap-1"
                aria-expanded={activeDropdown === 'legal'}
                aria-haspopup="true"
                onClick={() => toggleDropdown('legal')}
              >
                Legal
                <ChevronIcon open={activeDropdown === 'legal'} />
              </button>
              {activeDropdown === 'legal' && <DropdownMenu items={legalItems} />}
            </div>

            {/* Properties — separated with extra left margin */}
            <Link href="/properties" className="nav-link ml-3">
              Properties
            </Link>

            {/* Blog */}
            <Link href="/blog" className="nav-link ml-2">
              Blog
            </Link>

            {/* About */}
            <Link href="/about" className="nav-link ml-2">
              About
            </Link>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://www.pdalaw.co.uk"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs font-medium text-[#8A9BB0] hover:text-white transition-colors tracking-wide border-r border-white/15 pr-4"
            >
              PDA Law ↗
            </a>
            <Link href="/request-valuation" className="btn-primary text-sm py-2.5 px-5">
              Free Valuation
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white p-2 rounded-lg hover:bg-white/10 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
          >
            {menuOpen ? (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="3" x2="19" y2="19" />
                <line x1="19" y1="3" x2="3" y2="19" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="3" y1="6" x2="19" y2="6" />
                <line x1="3" y1="11" x2="19" y2="11" />
                <line x1="3" y1="16" x2="19" y2="16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-[#081525]/98 backdrop-blur-xl border-t border-white/8 px-6 py-6 flex flex-col gap-2">

            {/* Landlords accordion */}
            <div>
              <button
                className="w-full flex items-center justify-between text-white/80 hover:text-white font-medium text-base transition-colors py-2"
                onClick={() => setMobileExpanded(mobileExpanded === 'landlords' ? null : 'landlords')}
              >
                Landlords
                <ChevronIcon open={mobileExpanded === 'landlords'} />
              </button>
              {mobileExpanded === 'landlords' && (
                <div className="pl-4 flex flex-col gap-1 mt-1 mb-2">
                  {landlordItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="text-white/70 hover:text-white text-sm py-1.5 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Tenants accordion */}
            <div>
              <button
                className="w-full flex items-center justify-between text-white/80 hover:text-white font-medium text-base transition-colors py-2"
                onClick={() => setMobileExpanded(mobileExpanded === 'tenants' ? null : 'tenants')}
              >
                Tenants
                <ChevronIcon open={mobileExpanded === 'tenants'} />
              </button>
              {mobileExpanded === 'tenants' && (
                <div className="pl-4 flex flex-col gap-1 mt-1 mb-2">
                  {tenantItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="text-white/70 hover:text-white text-sm py-1.5 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Legal accordion */}
            <div>
              <button
                className="w-full flex items-center justify-between text-white/80 hover:text-white font-medium text-base transition-colors py-2"
                onClick={() => setMobileExpanded(mobileExpanded === 'legal' ? null : 'legal')}
              >
                Legal
                <ChevronIcon open={mobileExpanded === 'legal'} />
              </button>
              {mobileExpanded === 'legal' && (
                <div className="pl-4 flex flex-col gap-1 mt-1 mb-2">
                  {legalItems.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      className="text-white/70 hover:text-white text-sm py-1.5 transition-colors"
                      onClick={() => setMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Properties & Blog */}
            <Link
              href="/properties"
              className="text-white/80 hover:text-white font-medium text-base transition-colors py-2"
              onClick={() => setMenuOpen(false)}
            >
              Properties
            </Link>
            <Link
              href="/blog"
              className="text-white/80 hover:text-white font-medium text-base transition-colors py-2"
              onClick={() => setMenuOpen(false)}
            >
              Blog
            </Link>

            <Link
              href="/about"
              className="text-white/80 hover:text-white font-medium text-base transition-colors py-2"
              onClick={() => setMenuOpen(false)}
            >
              About
            </Link>

            <div className="border-t border-white/10 pt-4 mt-2 flex flex-col gap-3">
              <a
                href="https://www.pdalaw.co.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-[#8A9BB0] hover:text-white transition-colors"
              >
                PDA Law ↗
              </a>
              <Link
                href="/request-valuation"
                className="btn-primary text-sm justify-center"
                onClick={() => setMenuOpen(false)}
              >
                Free Valuation
              </Link>
            </div>
          </div>
        )}
      </header>
    </>
  );
}